import { useEffect, useMemo, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import {
  Building2,
  Download,
  FileText,
  IdCard,
  Package,
  PackageSearch,
  QrCode,
  RotateCcw,
  ScanLine,
  Shield,
  ShieldCheck,
  Truck,
  User,
  AlertCircle,
  CreditCard,
  Copy,
  Check,
  X,
} from 'lucide-react';
import AppLayout from '@components/AppLayout';
import client, { BASE_URL } from '../../src/api/client';
import '../styles/digital-id.css';
import QRCode from 'qrcode';
import { resolveQrReference } from '../../src/utils/qr';
import { getUnionBranding } from '../../src/utils/unionBranding';
import { getMemberAuthToken, getMemberAuthUserId } from '../../src/utils/memberAuth';

const formatMembershipDate = (value) => {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) {
    return value ?? 'N/A';
  }
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};

const getMemberInitials = (firstName = '', lastName = '') => {
  const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.trim().toUpperCase();
  return initials || 'LC';
};

const getDisplayName = (user) => {
  const fn = (user.firstName || '').trim();
  const ln = (user.lastName || '').trim();
  const mi = user.middleName ? `${user.middleName.charAt(0).toUpperCase()}.` : '';
  return [fn, mi, ln].filter(Boolean).join(' ').toUpperCase();
};

const getNameStyle = (fullName) => {
  const length = fullName.length;

  let fontSize = '1.5rem'; // Increased default base size

  // Revised scaling for better visibility while maintaining single line
  if (length > 46) {
    fontSize = '0.62rem';
  } else if (length > 40) {
    fontSize = '0.68rem';
  } else if (length > 35) {
    fontSize = '0.75rem'; 
  } else if (length > 28) {
    fontSize = '0.9rem'; 
  } else if (length > 22) {
    fontSize = '1.1rem'; // Increased for names like "Christian Dayne Menderico"
  } else if (length > 18) {
    fontSize = '1.25rem'; 
  } else if (length > 14) {
    fontSize = '1.35rem';
  }

  return {
    fontSize,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    width: '100%',
    maxWidth: '100%',
    display: 'block',
    textAlign: 'center',
    lineHeight: '1.2',
    margin: 0,
    letterSpacing: '0.05em', // Match the previous styling roughly
    color: '#0f172a',
    fontWeight: 700
  };
};

const normalizeOriginUrl = (value) => {
  if (typeof value !== 'string') {
    return '';
  }
  return value.trim().replace(/\/+$/, '');
};

const CANONICAL_SCAN_ORIGIN = 'https://www.laborconnect.app';
const LOCAL_SCAN_HOSTS = new Set(['localhost', '127.0.0.1', '0.0.0.0']);

const resolvePublicAppOrigin = () => {
  if (typeof window !== 'undefined' && window.location?.origin) {
    const currentHost = String(window.location.hostname || '').toLowerCase().trim();
    if (LOCAL_SCAN_HOSTS.has(currentHost)) {
      return normalizeOriginUrl(window.location.origin);
    }
  }

  return CANONICAL_SCAN_ORIGIN;
};

const buildScanVerificationUrl = (reference) => {
  const normalizedReference = typeof reference === 'string' ? reference.trim() : '';
  if (!normalizedReference) {
    return '';
  }

  const encodedReference = encodeURIComponent(normalizedReference);
  const origin = resolvePublicAppOrigin();
  if (!origin) {
    return `/scanqr?ref=${encodedReference}`;
  }

  return `${origin}/scanqr?ref=${encodedReference}`;
};

const formatStatus = (value) => {
  if (!value) return 'Pending';
  return value
    .toString()
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const normalizeCardStatus = (value) =>
  (value ?? '')
    .toString()
    .trim()
    .toLowerCase();

const PENDING_ID_CHECKOUT_KEY = 'alu_pending_id_checkout';
const DIGITAL_ID_BASE_WIDTH = 408;

const formatPeso = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '—';
  return `₱${num.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const ACTIVE_CARD_STATUSES = new Set(['pending', 'printing', 'ready_for_pickup', 'preparing', 'shipped', 'in_transit']);
const TERMINAL_CARD_STATUSES = new Set(['released', 'delivered', 'claimed', 'completed', 'cancelled', 'rejected', 'deleted']);
const LOCKED_ID_REQUEST_STATUSES = new Set(['preparing', 'shipped', 'in_transit', 'delivered', 'released']);
const SHIPPING_TRACKER_STATUSES = new Set(['preparing', 'shipped', 'in_transit', 'delivered', 'released']);

const isRequestActiveForMemberView = (statusValue) => {
  const normalized = normalizeCardStatus(statusValue);
  if (!normalized) return false;
  if (TERMINAL_CARD_STATUSES.has(normalized)) return false;
  if (ACTIVE_CARD_STATUSES.has(normalized)) return true;
  // Preserve existing behavior for unknown non-terminal statuses.
  return true;
};

const isIdRequestLockedForStatus = (statusValue) => {
  const normalized = normalizeCardStatus(statusValue);
  if (!normalized) return false;
  return LOCKED_ID_REQUEST_STATUSES.has(normalized);
};

export default function DigitalIdPage({ user, onLogout }) {
  const navigate = useNavigate();
  const cardScaleHostRef = useRef(null);
  const idCardRef = useRef(null);
  const idCardBackRef = useRef(null);
  const copyTimeoutRef = useRef(null);
  const [cardScale, setCardScale] = useState(1);
  const [showBack, setShowBack] = useState(false);
  const [showFlipHint, setShowFlipHint] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestLoading, setRequestLoading] = useState(false);
  const [requestForm, setRequestForm] = useState({
    deliveryMethod: 'pickup',
    useRegisteredAddress: true,
    deliveryAddress: '',
  });
  const [pricing, setPricing] = useState({
    baseFeePhp: 150,
    shippingFeePhp: 50,
    pickupTotalPhp: 150,
    deliveryTotalPhp: 200,
    loading: false,
    error: null,
  });
  const [isRequestPending, setIsRequestPending] = useState(
    Boolean(user?.hasPendingIdRequest || user?.hasAnyIdRequest),
  );
  const [latestCardStatus, setLatestCardStatus] = useState(null);
  const [latestShippingStatus, setLatestShippingStatus] = useState(null);
  const [statusModal, setStatusModal] = useState({
    open: false,
    requestId: null,
    status: null,
    statusLabel: null,
    deliveryMethod: null,
    paymentMethod: null,
    paymentMethodLabel: null,
    pickupCode: null,
    requestNo: null,
    paymentStatus: null,
    paymentStatusLabel: null,
    totalAmountPhp: null,
    baseFeePhp: null,
    shippingFeePhp: null,
    deliveryAddress: null,
    contactNumber: null,
    trackingNumber: null,
    shippingStatus: null,
    paymentReceiptUrl: null,
    paymentInvoiceUrl: null,
    paidAt: null,
    loading: false,
    error: null,
    requestedAt: null,
    updatedAt: null,
  });
  const [cashRequestModal, setCashRequestModal] = useState({
    open: false,
    message: '',
  });

  const effectiveMemberUserId = getMemberAuthUserId() || null;

  const getMemberRequestHeaders = () => {
    const token = getMemberAuthToken();
    if (!token) return {};
    return { Authorization: `Bearer ${token}` };
  };

  const storePendingCheckout = ({ sessionId, requestId }) => {
    if (!sessionId) return;
    try {
      sessionStorage.setItem(PENDING_ID_CHECKOUT_KEY, JSON.stringify({
        sessionId: String(sessionId),
        requestId: Number.isFinite(Number(requestId)) ? Number(requestId) : null,
        createdAt: Date.now(),
      }));
    } catch {
      // Ignore storage quota/privacy errors.
    }
  };

  const clearPendingCheckout = () => {
    try {
      sessionStorage.removeItem(PENDING_ID_CHECKOUT_KEY);
    } catch {
      // Ignore storage access errors.
    }
  };

  const loadPendingCheckout = () => {
    try {
      const raw = sessionStorage.getItem(PENDING_ID_CHECKOUT_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      const sessionId = (parsed?.sessionId ?? '').toString().trim();
      const requestId = Number(parsed?.requestId);
      const createdAt = Number(parsed?.createdAt);
      if (!sessionId) return null;
      return {
        sessionId,
        requestId: Number.isFinite(requestId) ? requestId : null,
        createdAt: Number.isFinite(createdAt) ? createdAt : null,
      };
    } catch {
      return null;
    }
  };

  useEffect(() => {
    let cancelled = false;

    const cancelPendingStripeSessionOnReturn = async () => {
      if (!effectiveMemberUserId) return;

      const pendingCheckout = loadPendingCheckout();
      if (!pendingCheckout?.sessionId) return;

      // Stale records should not keep trying forever.
      if (pendingCheckout.createdAt && (Date.now() - pendingCheckout.createdAt) > (6 * 60 * 60 * 1000)) {
        clearPendingCheckout();
        return;
      }

      try {
        await client.post(
          '/api/payments/cancel-session',
          {
            sessionId: pendingCheckout.sessionId,
            paymentFor: 'id_card_request',
            referenceId: pendingCheckout.requestId,
          },
          { headers: getMemberRequestHeaders() },
        );
      } catch (error) {
        // 409 usually means already paid/completed; treat as terminal and refresh status below.
        if (error?.response?.status && ![404, 409].includes(Number(error.response.status))) {
          console.warn('Unable to auto-cancel pending Stripe checkout on return:', error?.response?.data || error?.message);
        }
      } finally {
        clearPendingCheckout();
      }

      try {
        const response = await client.get(`/api/users/${effectiveMemberUserId}/id-card-status`, {
          headers: getMemberRequestHeaders(),
        });

        if (cancelled) return;
        const payload = response.data || {};
        const statusValue = payload.status || payload.state || payload.cardStatus || null;
        const shippingStatusValue = payload.shippingStatus || payload.shipping_status || null;
        setLatestCardStatus(statusValue);
        setLatestShippingStatus(shippingStatusValue);
        setIsRequestPending(isRequestActiveForMemberView(statusValue));
      } catch (error) {
        if (cancelled) return;
        if (Number(error?.response?.status) === 404) {
          setLatestCardStatus(null);
          setLatestShippingStatus(null);
          setIsRequestPending(false);
        }
      }
    };

    cancelPendingStripeSessionOnReturn();

    return () => {
      cancelled = true;
    };
  }, [effectiveMemberUserId]);

  useEffect(() => {
    const host = cardScaleHostRef.current;
    if (!host) {
      return undefined;
    }

    const updateCardScale = () => {
      const availableWidth = host.clientWidth;
      if (!availableWidth) {
        return;
      }

      const nextScale = Math.min(1, availableWidth / DIGITAL_ID_BASE_WIDTH);
      const normalizedScale = Number(nextScale.toFixed(4));

      setCardScale((prevScale) => {
        if (Math.abs(prevScale - normalizedScale) < 0.002) {
          return prevScale;
        }
        return normalizedScale;
      });
    };

    updateCardScale();

    const resizeObserver = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(updateCardScale)
      : null;

    if (resizeObserver) {
      resizeObserver.observe(host);
    }

    window.addEventListener('resize', updateCardScale);
    window.addEventListener('orientationchange', updateCardScale);

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      window.removeEventListener('resize', updateCardScale);
      window.removeEventListener('orientationchange', updateCardScale);
    };
  }, []);

  const selectedTotalPhp = useMemo(() => (
    requestForm.deliveryMethod === 'delivery'
      ? Number(pricing.deliveryTotalPhp || 0)
      : Number(pricing.pickupTotalPhp || 0)
  ), [pricing.deliveryTotalPhp, pricing.pickupTotalPhp, requestForm.deliveryMethod]);

  const registeredAddress = useMemo(() => {
    const parts = [
      user?.addressLine1 || user?.address || null,
      user?.addressLine2 || null,
      user?.barangay || null,
      user?.city || null,
      user?.province || null,
      user?.region || null,
      user?.postalCode || null,
    ].map((entry) => (entry ?? '').toString().trim()).filter(Boolean);

    return parts.length ? parts.join(', ') : '';
  }, [user]);

  useEffect(() => {
    if (requestForm.deliveryMethod !== 'delivery') {
      return;
    }

    if (requestForm.useRegisteredAddress) {
      setRequestForm((prev) => ({
        ...prev,
        deliveryAddress: registeredAddress,
      }));
    }
  }, [requestForm.deliveryMethod, requestForm.useRegisteredAddress, registeredAddress]);

  useEffect(() => {
    let cancelled = false;

    const loadPricing = async () => {
      if (!effectiveMemberUserId) return;

      try {
        setPricing((prev) => ({ ...prev, loading: true, error: null }));
        const response = await client.get(`/api/users/${effectiveMemberUserId}/id-card-pricing`, {
          headers: getMemberRequestHeaders(),
        });

        const payload = response.data?.pricing || {};
        if (cancelled) return;

        setPricing({
          baseFeePhp: Number(payload.baseFeePhp || 150),
          shippingFeePhp: Number(payload.shippingFeePhp || 50),
          pickupTotalPhp: Number(payload.pickupTotalPhp || payload.baseFeePhp || 150),
          deliveryTotalPhp: Number(payload.deliveryTotalPhp || 200),
          loading: false,
          error: null,
        });
      } catch (error) {
        if (cancelled) return;
        setPricing((prev) => ({
          ...prev,
          loading: false,
          error: error?.response?.data?.message || 'Unable to load ID card pricing.',
        }));
      }
    };

    loadPricing();

    return () => {
      cancelled = true;
    };
  }, [effectiveMemberUserId]);

  useEffect(() => {
    let cancelled = false;

    const syncRequestStatus = async () => {
      if (!effectiveMemberUserId) {
        if (!cancelled) {
          setIsRequestPending(false);
        }
        return;
      }

      const hasCachedRequest = Boolean(user?.hasPendingIdRequest || user?.hasAnyIdRequest);
      if (hasCachedRequest && !cancelled) {
        setIsRequestPending(true);
      }

      try {
        const response = await client.get(`/api/users/${effectiveMemberUserId}/id-card-status`, {
          headers: getMemberRequestHeaders(),
        });
        const payload = response.data || {};
        const statusValue = payload.status || payload.state || payload.cardStatus || null;
        const shippingStatusValue = payload.shippingStatus || payload.shipping_status || null;
        setLatestCardStatus(statusValue);
        setLatestShippingStatus(shippingStatusValue);
        if (!cancelled) {
          setIsRequestPending(isRequestActiveForMemberView(statusValue));
        }
      } catch (err) {
        if (cancelled) return;
        if (err?.response?.status === 404) {
          setLatestCardStatus(null);
          setLatestShippingStatus(null);
          setIsRequestPending(false);
          return;
        }
        console.error('Unable to sync ID card request status', err);
      }
    };

    syncRequestStatus();

    return () => {
      cancelled = true;
    };
  }, [effectiveMemberUserId, user?.hasAnyIdRequest, user?.hasPendingIdRequest]);

  useEffect(() => {
    const hintTimer = window.setTimeout(() => {
      setShowFlipHint(true);
    }, 220);

    const clearHintTimer = window.setTimeout(() => {
      setShowFlipHint(false);
    }, 2200);

    return () => {
      window.clearTimeout(hintTimer);
      window.clearTimeout(clearHintTimer);
    };
  }, []);

  const openStatusModal = async () => {
    if (!effectiveMemberUserId) {
      setStatusModal((prev) => ({
        ...prev,
        open: true,
        loading: false,
        error: 'Unable to resolve your member session. Please log in again.',
      }));
      return;
    }

    setStatusModal((prev) => ({ ...prev, open: true, loading: true, error: null }));
    try {
      const response = await client.get(`/api/users/${effectiveMemberUserId}/id-card-status`, {
        headers: getMemberRequestHeaders(),
      });
      const payload = response.data || {};
      const statusValue = payload.status || payload.state || payload.cardStatus || 'pending';
      const normalizedStatus = normalizeCardStatus(statusValue);
      const shippingStatusValue = payload.shippingStatus || payload.shipping_status || null;
      setLatestCardStatus(statusValue);
      setLatestShippingStatus(shippingStatusValue);
      setIsRequestPending(isRequestActiveForMemberView(statusValue));
      const pickupValue = normalizedStatus === 'released'
        ? null
        : (payload.pickupCode || payload.metadata?.pickupCode || payload.pickup_code || null);
      const requestNoValue = payload.requestNo || payload.request_no || null;
      const paymentStatusValue = payload.paymentStatus || payload.payment_status || null;
      setStatusModal({
        open: true,
        loading: false,
        error: null,
        requestId: payload.id ?? payload.requestId ?? null,
        status: statusValue,
        statusLabel: payload.statusLabel || null,
        deliveryMethod: payload.deliveryMethod || payload.delivery_method || null,
        paymentMethod: payload.paymentMethod || payload.payment_method || null,
        paymentMethodLabel: payload.paymentMethodLabel || null,
        pickupCode: pickupValue,
        requestNo: requestNoValue,
        paymentStatus: paymentStatusValue,
        paymentStatusLabel: payload.paymentStatusLabel || null,
        totalAmountPhp: payload.pricing?.totalAmountPhp ?? null,
        baseFeePhp: payload.pricing?.baseFeePhp ?? null,
        shippingFeePhp: payload.pricing?.shippingFeePhp ?? null,
        deliveryAddress: payload.deliveryAddress || null,
        contactNumber: payload.contactNumber || payload.metadata?.phone || null,
        trackingNumber: payload.trackingNumber || null,
        shippingStatus: payload.shippingStatus || null,
        paymentReceiptUrl: payload.paymentReceiptUrl || null,
        paymentInvoiceUrl: payload.paymentInvoiceUrl || null,
        paidAt: payload.paidAt || null,
        requestedAt: payload.requestedAt || null,
        updatedAt: payload.updatedAt || null,
        rejectionReason: payload.rejectionReason || payload.metadata?.rejection?.reason || null,
        rejectedAt: payload.rejectedAt || payload.metadata?.rejection?.rejectedAt || null,
        refundId: payload.refundId || payload.metadata?.rejection?.refund?.refundId || null,
        refundStatus: payload.refundStatus || payload.metadata?.rejection?.refund?.refundStatus || null,
        refundAmountPhp: payload.refundAmountPhp || payload.metadata?.rejection?.refund?.amountPhp || null,
      });
    } catch (err) {
      console.error('Unable to load status', err);
      if (err?.response?.status === 401) {
        setStatusModal((prev) => ({ ...prev, open: false, loading: false, error: null }));
        return;
      }
      if (err?.response?.status === 404) {
        setLatestCardStatus(null);
        setLatestShippingStatus(null);
        setIsRequestPending(false);
        setStatusModal((prev) => ({ ...prev, open: false, loading: false, error: null }));
        return;
      }
      setStatusModal((prev) => ({
        ...prev,
        loading: false,
        error: 'Unable to load status. Please try again.',
      }));
    }
  };

  const closeStatusModal = () =>
    setStatusModal({
      open: false,
      requestId: null,
      status: null,
      statusLabel: null,
      deliveryMethod: null,
      paymentMethod: null,
      paymentMethodLabel: null,
      pickupCode: null,
      requestNo: null,
      paymentStatus: null,
      paymentStatusLabel: null,
      totalAmountPhp: null,
      baseFeePhp: null,
      shippingFeePhp: null,
      deliveryAddress: null,
      contactNumber: null,
      trackingNumber: null,
      shippingStatus: null,
      paymentReceiptUrl: null,
      paymentInvoiceUrl: null,
      paidAt: null,
      loading: false,
      error: null,
      requestedAt: null,
      updatedAt: null,
      rejectionReason: null,
      rejectedAt: null,
      refundId: null,
      refundStatus: null,
      refundAmountPhp: null,
    });
  const closeCashRequestModal = () =>
    setCashRequestModal({
      open: false,
      message: '',
    });

  const openCashRequestModal = (message) =>
    setCashRequestModal({
      open: true,
      message: message || 'ID request submitted. Please proceed to office payment on pickup.',
    });
  const [copiedReference, setCopiedReference] = useState(false);

  const startIdCardCheckout = async (requestId) => {
    if (!effectiveMemberUserId) {
      throw new Error('Unable to resolve your member session. Please log in again.');
    }

    const token = getMemberAuthToken();
    const authHeaders = token
      ? { Authorization: `Bearer ${token}` }
      : {};

    const checkoutResponse = await client.post(
      '/api/payments/checkout-session',
      {
        paymentFor: 'id_card_request',
        referenceId: requestId,
        userId: effectiveMemberUserId,
      },
      {
        headers: authHeaders,
      },
    );

    const checkoutUrl = checkoutResponse?.data?.checkoutUrl;
    const sessionId = checkoutResponse?.data?.sessionId;
    if (!checkoutUrl) {
      throw new Error('Secure checkout URL is missing.');
    }

    storePendingCheckout({ sessionId, requestId });

    window.location.assign(checkoutUrl);
  };

  const handleDownloadPDF = async () => {
    // Check references but with logging to debug if missing
    if (!idCardRef.current || !idCardBackRef.current) {
      console.error('Refs missing:', { front: !!idCardRef.current, back: !!idCardBackRef.current });
      return;
    }

    try {
      // Wait for any pending renders
      await new Promise(resolve => setTimeout(resolve, 100));

      // 1. Capture dimensions from the live DOM (Front & Back)
      const frontRect = idCardRef.current.getBoundingClientRect();
      const backRect = idCardBackRef.current.getBoundingClientRect();
      const cardInner = idCardRef.current.closest('.digital-id-card__inner');
      const innerRect = cardInner?.getBoundingClientRect?.();

      // Use scrollHeight to ensure we capture full content including signature
      const frontW = idCardRef.current.offsetWidth || frontRect.width;
      const frontH = Math.max(
        idCardRef.current.offsetHeight,
        idCardRef.current.scrollHeight,
        frontRect.height,
      );

      const backW = idCardBackRef.current.offsetWidth || backRect.width || frontW;
      const backH = Math.max(
        idCardBackRef.current.offsetHeight,
        idCardBackRef.current.scrollHeight,
        backRect.height,
        frontH,
      );

      const sharedW = Math.max(
        cardInner?.offsetWidth || 0,
        innerRect?.width || 0,
        frontW,
        backW,
      );
      const sharedH = Math.max(
        cardInner?.offsetHeight || 0,
        innerRect?.height || 0,
        frontH,
        backH,
      );

      const scale = 3;

      const buildCaptureOptions = (targetFace, width, height) => ({
        scale,
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
        width,
        height,
        onclone: (clonedDoc) => {
          const card = clonedDoc.querySelector('.digital-id-card');
          const scaleFrame = clonedDoc.querySelector('.digital-id-card__scale-frame');
          const inner = clonedDoc.querySelector('.digital-id-card__inner');
          const frontFace = clonedDoc.querySelector('.digital-id-card__face--front');
          const backFace = clonedDoc.querySelector('.digital-id-card__face--back');

          if (card) {
            card.classList.remove('digital-id-card--flipped');
            card.style.setProperty('--digital-id-scale', '1');
            card.style.width = `${width}px`;
            card.style.maxWidth = `${width}px`;
            card.style.height = `${height}px`;
            card.style.minHeight = `${height}px`;
          }

          if (scaleFrame) {
            scaleFrame.style.position = 'relative';
            scaleFrame.style.left = '0';
            scaleFrame.style.top = '0';
            scaleFrame.style.width = `${width}px`;
            scaleFrame.style.height = `${height}px`;
            scaleFrame.style.aspectRatio = 'auto';
            scaleFrame.style.transform = 'none';
            scaleFrame.style.transition = 'none';
          }

          if (inner) {
            inner.style.width = `${width}px`;
            inner.style.height = `${height}px`;
            inner.style.aspectRatio = 'auto';
            inner.style.transform = 'none';
            inner.style.transition = 'none';
          }

          if (frontFace) {
            frontFace.style.width = `${width}px`;
            frontFace.style.height = `${height}px`;
            frontFace.style.minHeight = `${height}px`;
            frontFace.style.transform = 'none';
            frontFace.style.backfaceVisibility = 'visible';
            frontFace.style.visibility = targetFace === 'front' ? 'visible' : 'hidden';
            frontFace.style.display = targetFace === 'front' ? 'flex' : 'none';
          }

          if (backFace) {
            backFace.style.width = `${width}px`;
            backFace.style.height = `${height}px`;
            backFace.style.minHeight = `${height}px`;
            backFace.style.transform = 'none';
            backFace.style.backfaceVisibility = 'visible';
            backFace.style.visibility = targetFace === 'back' ? 'visible' : 'hidden';
            backFace.style.display = targetFace === 'back' ? 'flex' : 'none';
            backFace.style.position = 'relative';
            backFace.style.left = '0';
            backFace.style.top = '0';
            backFace.style.margin = '0';
          }

          const sealImages = clonedDoc.querySelectorAll('.digital-id-card__seal img');
          sealImages.forEach((img) => {
            img.style.width = '76px';
            img.style.height = '76px';
            img.style.maxWidth = '76px';
            img.style.maxHeight = '76px';
            img.style.objectFit = 'contain';
            img.style.objectPosition = 'center';
            img.style.display = 'block';
          });
        },
      });

      // 2. Capture Front with full height
      const canvasFront = await html2canvas(idCardRef.current, {
        ...buildCaptureOptions('front', sharedW, sharedH),
      });

      // 3. Capture Back with explicit sizing
      const canvasBack = await html2canvas(idCardBackRef.current, {
        ...buildCaptureOptions('back', sharedW, sharedH),
      });

      // 4. Calculate PDF Size (ID-1 card standard: 85.6mm × 53.98mm, aspect ratio 1:1.586)
      const imgWidth = 85.6; // Standard ID-1 width in mm
      const ratio = canvasFront.height / canvasFront.width;
      const imgHeight = imgWidth * ratio;

      const margin = 10;
      const pdfWidth = imgWidth + (margin * 2);
      const pdfHeight = imgHeight + (margin * 2);

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: [pdfWidth, pdfHeight],
      });

      // Page 1: Front
      const imgDataFront = canvasFront.toDataURL('image/png');
      pdf.addImage(imgDataFront, 'PNG', margin, margin, imgWidth, imgHeight);

      // Page 2: Back
      pdf.addPage([pdfWidth, pdfHeight], 'portrait');
      const imgDataBack = canvasBack.toDataURL('image/png');
      pdf.addImage(imgDataBack, 'PNG', margin, margin, imgWidth, imgHeight);

      pdf.save(`LWU-ID-${user.digitalId || 'Card'}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Could not generate PDF. Please try again.');
    }
  };

  const handleRequestPhysicalID = async () => {
    if (isRequestActionLocked) {
      alert('You cannot request another physical ID while the current one is preparing, shipped, or delivered.');
      return;
    }

    if (!effectiveMemberUserId) {
      alert('Unable to resolve your member session. Please log in again.');
      return;
    }

    try {
      setRequestLoading(true);

      const isDelivery = requestForm.deliveryMethod === 'delivery';
      const resolvedDeliveryAddress = requestForm.useRegisteredAddress
        ? registeredAddress
        : (requestForm.deliveryAddress || '').trim();

      if (isDelivery && resolvedDeliveryAddress.length < 8) {
        alert('Please provide a valid delivery address (at least 8 characters).');
        setRequestLoading(false);
        return;
      }

      const requestResponse = await client.post(
        `/api/users/${effectiveMemberUserId}/request-id-card`,
        {
          deliveryMethod: requestForm.deliveryMethod,
          useRegisteredAddress: Boolean(requestForm.useRegisteredAddress),
          deliveryAddress: isDelivery ? resolvedDeliveryAddress : null,
        },
        { headers: getMemberRequestHeaders() },
      );
      const requestId = Number(requestResponse?.data?.requestId);

      if (!Number.isFinite(requestId)) {
        throw new Error('ID request was created, but no request id was returned for payment.');
      }

      setIsRequestPending(true);
      setShowRequestModal(false);

      const checkoutUrl = requestResponse?.data?.checkoutUrl;
      const checkoutSessionId = requestResponse?.data?.sessionId;
      if (checkoutUrl) {
        storePendingCheckout({ sessionId: checkoutSessionId, requestId });
        window.location.assign(checkoutUrl);
        return;
      }

      openCashRequestModal(requestResponse?.data?.message || 'ID request submitted. Please proceed to office payment on pickup.');
    } catch (error) {
      setShowRequestModal(false);
      if (error.response?.status === 400 && error.response?.data?.message?.includes('already')) {
        setIsRequestPending(true);
        await openStatusModal();
      } else {
        alert(error.response?.data?.message || 'Failed to submit request.');
      }
    } finally {
      setRequestLoading(false);
    }
  };

  const handleRetryIdPayment = async () => {
    const requestId = Number(statusModal.requestId);
    if (!Number.isFinite(requestId)) {
      alert('No request id found for payment retry. Please refresh the status and try again.');
      return;
    }

    try {
      setRequestLoading(true);
      await startIdCardCheckout(requestId);
    } catch (error) {
      alert(error?.response?.data?.message || error.message || 'Unable to start payment retry.');
    } finally {
      setRequestLoading(false);
    }
  };

  const handleDownloadQrJpeg = async () => {
    const qrSource = generatedQr || qrImage;
    if (!qrSource) {
      alert('QR code is not available yet. Please try again in a moment.');
      return;
    }

    try {
      const image = new Image();
      image.crossOrigin = 'anonymous';

      await new Promise((resolve, reject) => {
        image.onload = resolve;
        image.onerror = reject;
        image.src = qrSource;
      });

      const width = image.naturalWidth || 280;
      const height = image.naturalHeight || 280;
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const context = canvas.getContext('2d');
      if (!context) {
        throw new Error('Unable to create canvas context.');
      }

      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, width, height);
      context.drawImage(image, 0, 0, width, height);

      const blob = await new Promise((resolve) => {
        canvas.toBlob((result) => resolve(result), 'image/jpeg', 0.95);
      });

      const fileName = `LWU-ID-QR-${shortMemberId || user?.id || 'member'}.jpg`;

      if (blob) {
        const objectUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(objectUrl);
        return;
      }

      const fallbackDataUrl = canvas.toDataURL('image/jpeg', 0.95);
      const fallbackLink = document.createElement('a');
      fallbackLink.href = fallbackDataUrl;
      fallbackLink.download = fileName;
      document.body.appendChild(fallbackLink);
      fallbackLink.click();
      document.body.removeChild(fallbackLink);
    } catch (error) {
      console.error('Unable to download QR as JPEG', error);
      alert('Unable to download QR code. Please try again.');
    }
  };

  const profilePhoto = useMemo(() => {
    const source = user.photo || user.profilePicture || user.avatar || null;
    if (!source) return null;
    if (source.startsWith('data:') || source.startsWith('http')) return source;
    return `${BASE_URL}${source}`;
  }, [user.photo, user.profilePicture, user.avatar]);

  const qrImage = useMemo(() => {
    if (!user.qrCode) return null;
    if (user.qrCode.startsWith('data:') || user.qrCode.startsWith('http')) {
      return user.qrCode;
    }
    return `${BASE_URL}${user.qrCode}`;
  }, [user.qrCode]);

  const signatureImage = useMemo(() => {
    if (!user.signature) return null;
    if (user.signature.startsWith('data:') || user.signature.startsWith('http')) {
      return user.signature;
    }
    return `${BASE_URL}${user.signature}`;
  }, [user.signature]);

  const qrReference = useMemo(() => resolveQrReference(user), [user]);
  const branding = getUnionBranding(user.unionAffiliation);
  const watermarkLabel = branding.shortName || 'LaborConnect';
  const headerWatermarkStyle = useMemo(() => ({
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Ctext x='18' y='30' fill='rgba(248,250,252,0.16)' font-size='18' font-family='Arial, sans-serif' font-weight='700'%3E${encodeURIComponent(watermarkLabel)}%3C/text%3E%3Ctext x='18' y='58' fill='rgba(248,250,252,0.14)' font-size='16' font-family='Arial, sans-serif' font-weight='600'%3ETUCP%3C/text%3E%3Ctext x='18' y='84' fill='rgba(248,250,252,0.12)' font-size='12' font-family='Arial, sans-serif' font-weight='600'%3E1954%3C/text%3E%3C/svg%3E")`,
  }), [watermarkLabel]);
  const bodyWatermarkStyle = useMemo(() => ({
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120' viewBox='0 0 120 120'%3E%3Ctext x='10' y='30' fill='rgba(79,70,229,0.08)' font-size='22' font-family='Arial, sans-serif' font-weight='700'%3E${encodeURIComponent(watermarkLabel)}%3C/text%3E%3Ctext x='10' y='60' fill='rgba(79,70,229,0.08)' font-size='14' font-family='Arial, sans-serif' font-weight='600'%3EOFFICIAL%3C/text%3E%3Ctext x='10' y='86' fill='rgba(15,23,42,0.08)' font-size='12' font-family='Arial, sans-serif' font-weight='600'%3EMEMBER%3C/text%3E%3C/svg%3E")`,
  }), [watermarkLabel]);
  const companyName = user.company || user.union || 'LaborConnect Member';
  const unionName = user.unionName || user.unionAffiliation || user.union || 'LaborConnect';
  // Use unionPosition first (President, Vice President, etc.), then position, then default to Member
  const memberPosition = user.unionPosition || user.position || 'Member';
  const emergencyContactName = (user?.emergencyContact?.name || user?.emergencyContactName || '').toString().trim() || 'Not Provided';
  const emergencyContactMobile = (
    user?.emergencyContact?.phone
    || user?.emergencyContactPhone
    || user?.emergencyContactMobile
    || user?.emergencyContactNumber
    || ''
  ).toString().trim() || 'Not Provided';

  const memberId = useMemo(() => {
    if (!user.digitalId) return 'LWU-000000';
    return user.digitalId;
  }, [user.digitalId]);

  const shortMemberId = useMemo(() => memberId.split('-').pop() ?? memberId, [memberId]);

  const qrDataString = useMemo(() => buildScanVerificationUrl(qrReference), [qrReference]);

  const [generatedQr, setGeneratedQr] = useState('');

  useEffect(() => {
    let cancelled = false;

    if (!qrDataString) {
      setGeneratedQr('');
      return () => {
        cancelled = true;
      };
    }

    QRCode.toDataURL(qrDataString, { width: 280, margin: 1 })
      .then((url) => {
        if (!cancelled) {
          setGeneratedQr(url);
        }
      })
      .catch((error) => {
        console.error('Unable to generate QR code', error);
        if (!cancelled) {
          setGeneratedQr('');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [qrDataString]);

  useEffect(() => () => {
    if (copyTimeoutRef.current) {
      clearTimeout(copyTimeoutRef.current);
    }
  }, []);

  const handleCopyQrReference = async () => {
    if (!qrReference) {
      return;
    }

    try {
      const canUseClipboard = typeof navigator !== 'undefined' && navigator.clipboard?.writeText;
      if (canUseClipboard) {
        await navigator.clipboard.writeText(qrReference);
      } else if (typeof document !== 'undefined') {
        const textarea = document.createElement('textarea');
        textarea.value = qrReference;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'absolute';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }

      setCopiedReference(true);
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
      copyTimeoutRef.current = setTimeout(() => {
        setCopiedReference(false);
        copyTimeoutRef.current = null;
      }, 2000);
    } catch (error) {
      console.error('Unable to copy QR reference', error);
      setCopiedReference(false);
    }
  };

  const isVerified = user?.isApproved === 'active' || user?.isApproved === 'approved' || user?.isApproved === true || user?.isApproved === 1 || user?.isApproved === '1';
  const isIncomplete = user?.isApproved === 'incomplete' || ((user?.isApproved === 'pending' || user?.isApproved === 0 || user?.isApproved === false) && !user?.dateOfBirth);
  const normalizedLatestCardStatus = normalizeCardStatus(latestCardStatus);
  const normalizedLatestShippingStatus = normalizeCardStatus(latestShippingStatus);
  const lockStatusSource = normalizedLatestShippingStatus || normalizedLatestCardStatus;
  const isRequestActionLocked = isIdRequestLockedForStatus(lockStatusSource);
  const shouldShowShippingTracker = SHIPPING_TRACKER_STATUSES.has(normalizedLatestShippingStatus)
    || SHIPPING_TRACKER_STATUSES.has(normalizedLatestCardStatus);
  const showDeliveredStyleActions = isRequestActionLocked || !isRequestPending;
  const canOpenStatusPanel = shouldShowShippingTracker;
  const statusPanelButtonLabel = showDeliveredStyleActions ? 'Preview status panel' : 'Open status details';
  const trackerStage = normalizedLatestShippingStatus || normalizedLatestCardStatus;
  const shippingProgressKey = trackerStage === 'preparing'
    ? 'preparing'
    : (trackerStage === 'shipped' || trackerStage === 'in_transit')
      ? 'shipping'
      : 'delivered';
  const shippingTrackerPreviewSteps = [
    {
      key: 'preparing',
      label: 'Preparing',
      icon: Package,
      state: shippingProgressKey === 'preparing' ? 'current' : 'done',
    },
    {
      key: 'shipping',
      label: 'Shipping',
      icon: Truck,
      state: shippingProgressKey === 'shipping' ? 'current' : (shippingProgressKey === 'preparing' ? 'pending' : 'done'),
    },
    {
      key: 'delivered',
      label: 'Delivered',
      icon: Check,
      state: shippingProgressKey === 'delivered' ? 'current' : 'pending',
    },
  ];

  if (!isVerified) {
    return (
      <AppLayout title="Digital Union ID" user={user} onLogout={onLogout}>
        <div className="digital-id-page">
          <div className="rejection-lockout" style={{
            maxWidth: '600px',
            margin: '64px auto',
            background: 'var(--color-surface, #ffffff)',
            padding: '48px',
            borderRadius: '24px',
            textAlign: 'center',
            boxShadow: 'var(--shadow-md, 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06))',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{
              width: '72px',
              height: '72px',
              background: '#FFFBEB',
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              margin: '0 auto 24px',
              color: '#F59E0B'
            }}>
              <AlertCircle size={36} />
            </div>
            <h1 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '16px', color: '#111827' }}>Verification Required</h1>
            <p style={{ color: '#6B7280', fontSize: '16px', marginBottom: '32px', lineHeight: '1.6' }}>
              {isIncomplete
                ? "Your membership profile is incomplete. Please verify your details to access your Digital ID."
                : "Your membership application is still under review. Once approved, you will gain access to your Digital ID."}
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              {isIncomplete ? (
                <button
                  type="button"
                  onClick={() => navigate('/complete-profile')}
                  style={{
                    backgroundColor: '#2563eb',
                    color: 'white',
                    padding: '12px 24px',
                    borderRadius: '999px',
                    border: 'none',
                    fontWeight: '600',
                    fontSize: '15px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  Complete Verification
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => navigate('/membership-form')}
                  style={{
                    backgroundColor: '#2563eb',
                    color: 'white',
                    padding: '12px 24px',
                    borderRadius: '999px',
                    border: 'none',
                    fontWeight: '600',
                    fontSize: '15px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  View Application
                </button>
              )}
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                style={{
                  backgroundColor: 'white',
                  color: '#374151',
                  padding: '12px 24px',
                  borderRadius: '999px',
                  border: '1px solid #d1d5db',
                  fontWeight: '600',
                  fontSize: '15px',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout title="Digital Union ID" user={user} onLogout={onLogout}>
      <div className="digital-id-page">
        <div className="digital-id-page__layout">
          <section className="digital-id-page__column digital-id-page__column--card">
            <section
              className={`digital-id-card${showBack ? ' digital-id-card--flipped' : ''}${showFlipHint && !showBack ? ' digital-id-card--flip-hint' : ''}`}
              ref={cardScaleHostRef}
              style={{ '--digital-id-scale': String(cardScale) }}
              onClick={() => setShowBack((prev) => !prev)}
              role="presentation"
            >
              <div className="digital-id-card__scale-frame">
                <div className="digital-id-card__inner">
                  <div className="digital-id-card__face digital-id-card__face--front" ref={idCardRef}>
                <div className="digital-id-card__header" style={{ background: branding.colors.headerGradient }}>
                  <div className="digital-id-card__header-watermark" style={headerWatermarkStyle} />
                  <div className="digital-id-card__seal">
                    <img src={branding.logo} alt={branding.shortName} />
                  </div>
                  <div>
                    <p style={{ fontSize: branding.fullName.length > 30 ? '0.78rem' : '0.9rem' }}>{branding.fullName.toUpperCase()}</p>
                    <span>No. 262, 15th Ave., Brgy. Silangan, Cubao, Quezon City</span>
                  </div>
                </div>
                <div className="digital-id-card__member-banner">{memberPosition.toUpperCase()}</div>
                <div className="digital-id-card__body">
                  <div className="digital-id-card__body-watermark" style={bodyWatermarkStyle} />
                  <div className="digital-id-card__photo">
                    {profilePhoto ? (
                      <img src={profilePhoto} alt="Member" />
                    ) : (
                      <div className="digital-id-card__initials">{getMemberInitials(user.firstName, user.lastName)}</div>
                    )}
                  </div>
                  <div className="digital-id-card__info" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <div style={getNameStyle(getDisplayName(user))}>
                      {getDisplayName(user)}
                    </div>
                    <div style={{ 
                      fontSize: '0.75rem', 
                      fontWeight: '700', 
                      color: '#475569', 
                      textAlign: 'center', 
                      textTransform: 'uppercase', 
                      letterSpacing: '0.05em'
                    }}>
                      {memberPosition}
                    </div>
                    <div className="digital-id-card__meta" style={{ width: '100%', marginTop: '20px' }}>
                      <table style={{ 
                          margin: '0 auto', 
                          fontSize: '0.8rem', 
                          color: '#334155', 
                          borderSpacing: '0 2px', 
                          borderCollapse: 'separate',
                          textAlign: 'left'
                      }}>
                        <tbody>
                          <tr>
                            <td style={{ paddingRight: '10px', fontWeight: '500', color: '#64748b' }}>ID Number</td>
                            <td style={{ paddingRight: '10px', color: '#64748b' }}>:</td>
                            <td style={{ fontWeight: '600', color: '#0f172a' }}>{shortMemberId}</td>
                          </tr>
                          <tr>
                            <td style={{ paddingRight: '10px', fontWeight: '500', color: '#64748b' }}>Company</td>
                            <td style={{ paddingRight: '10px', color: '#64748b' }}>:</td>
                            <td style={{ fontWeight: '600', color: '#0f172a' }}>{companyName}</td>
                          </tr>
                          <tr>
                            <td style={{ paddingRight: '10px', fontWeight: '500', color: '#64748b' }}>Contact No.</td>
                            <td style={{ paddingRight: '10px', color: '#64748b' }}>:</td>
                            <td style={{ fontWeight: '600', color: '#0f172a' }}>{user.phone || 'N/A'}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div className="digital-id-card__signature" style={{ position: 'relative', marginTop: 'auto', paddingTop: '16px', width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                      {signatureImage && (
                        <img
                          src={signatureImage}
                          alt="Signature"
                          style={{
                            position: 'absolute',
                            bottom: '10px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            height: '80px',
                            maxWidth: '90%',
                            objectFit: 'contain',
                            pointerEvents: 'none',
                            zIndex: 10,
                          }}
                        />
                      )}
                      <div style={{ width: '70%', height: '1px', background: 'rgba(15, 23, 42, 0.6)' }}></div>
                      <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'rgba(15, 23, 42, 0.7)', textAlign: 'center' }}>Authorized Signature</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="digital-id-card__face digital-id-card__face--back" ref={idCardBackRef}>
                <div className="digital-id-card__header digital-id-card__header--back" style={{ background: branding.colors.headerBackGradient }}>
                  <div className="digital-id-card__header-watermark" style={headerWatermarkStyle} />
                  <div className="digital-id-card__seal">
                    <img src={branding.logo} alt={branding.shortName} />
                  </div>
                  <div>
                    <p>{branding.shortName} DIGITAL ID</p>
                    <span>Official Verification Code</span>
                  </div>
                </div>
                <div className="digital-id-card__qr">
                  <div className="digital-id-card__qr-box">
                    {(generatedQr || qrImage) ? (
                      <img src={generatedQr || qrImage} alt="Membership QR" />
                    ) : (
                      <QrCode size={168} />
                    )}
                  </div>
                  <div className="digital-id-card__qr-info">
                    <span>{memberId}</span>
                    <span style={{ fontSize: '0.8rem', fontFamily: 'var(--font-family-mono)', color: '#6b7280' }}>QR Ref: {qrReference}</span>
                    <small>Scan to verify membership status</small>
                  </div>
                  <div className="digital-id-card__emergency-contact">
                    <strong>Emergency Contact</strong>
                    <span>Name: {emergencyContactName}</span>
                    <span>Mobile No: {emergencyContactMobile}</span>
                  </div>
                </div>
                <div className="digital-id-card__footer" style={{ background: branding.colors.footerGradient }}>
                  <ShieldCheck size={16} />
                  <span>{branding.shortName || 'LaborConnect'} © 2026 • Official Digital Identification</span>
                </div>
              </div>
                </div>
                </div>
            </section>
            <button
              type="button"
              className="digital-id-page__card-toggle"
              onClick={() => setShowBack((prev) => !prev)}
            >
              <RotateCcw size={18} />
              {showBack ? 'Show Front' : 'Show QR'}
            </button>
          </section>

          <aside className="digital-id-page__column digital-id-page__column--manage">
            <div className="digital-id-sidebar__card">
              <h3>Manage your digital ID</h3>
              <div className="digital-id-sidebar__actions">
                <button
                  type="button"
                  className="digital-id-sidebar__action"
                  onClick={handleCopyQrReference}
                  disabled={!qrReference}
                >
                  {copiedReference ? <Check size={16} /> : <ScanLine size={16} />}
                  {copiedReference ? 'Copied!' : `QR-${shortMemberId}`}
                  {copiedReference ? null : <Copy size={14} style={{ marginLeft: 'auto' }} />}
                </button>
                <button type="button" className="digital-id-sidebar__action" onClick={handleDownloadPDF}>
                  <FileText size={16} />
                  Download PDF copy
                  <Download size={14} style={{ marginLeft: 'auto' }} />
                </button>
                <button type="button" className="digital-id-sidebar__action" onClick={handleDownloadQrJpeg}>
                  <QrCode size={16} />
                  Download QR
                  <Download size={14} style={{ marginLeft: 'auto' }} />
                </button>
              </div>
              {isRequestPending && !showDeliveredStyleActions ? (
                <button
                  type="button"
                  className="digital-id-sidebar__request-btn"
                  onClick={openStatusModal}
                >
                  <PackageSearch size={16} />
                  Open status details
                </button>
              ) : (
                <button
                  type="button"
                  className="digital-id-sidebar__request-btn"
                  onClick={() => setShowRequestModal(true)}
                  disabled={requestLoading || isRequestActionLocked}
                >
                  <IdCard size={16} />
                  Request Physical ID
                </button>
              )}
            </div>

            <div className="digital-id-sidebar__card">
              <h3>How to use your ID</h3>
              <ul>
                <li>
                  <span className="dot dot--blue" />
                  Present during union assemblies, seminars, and benefit claims.
                </li>
                <li>
                  <span className="dot dot--green" />
                  Use the QR code for quick verification at partner offices.
                </li>
                <li>
                  <span className="dot dot--purple" />
                  Keep your profile photo updated for identification.
                </li>
                <li>
                  <span className="dot dot--orange" />
                  Report lost devices immediately to revoke access.
                </li>
              </ul>
            </div>
          </aside>

          <aside className="digital-id-page__column digital-id-page__column--status">
            <div className="digital-id-sidebar__card">
              <h3>Physical ID Status</h3>
              {shouldShowShippingTracker ? (
                <div className="digital-id-shipping-tracker" aria-label="Shipping tracker preview">
                  <span className="digital-id-shipping-tracker__title">Shipping Tracker</span>
                  <div className="digital-id-shipping-tracker__steps">
                    {shippingTrackerPreviewSteps.map((step) => {
                      const Icon = step.icon;
                      return (
                        <div
                          key={step.key}
                          className={`digital-id-shipping-tracker__step${step.state === 'done' ? ' is-done' : ''}${step.state === 'current' ? ' is-current' : ''}`}
                        >
                          <span className="digital-id-shipping-tracker__icon">
                            <Icon size={15} />
                          </span>
                          <span className="digital-id-shipping-tracker__label">{step.label}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : isRequestPending ? (
                <div className="digital-id-sidebar__status-placeholder">
                  <p>Your physical ID request is currently pending.</p>
                  <p>Tracker becomes available once your request moves to preparing or shipping.</p>
                </div>
              ) : (
                <div className="digital-id-sidebar__status-placeholder">
                  <p>No physical ID request in progress.</p>
                  <p>Submit a request to start your ID processing and shipping updates.</p>
                </div>
              )}
              <button
                type="button"
                className="digital-id-sidebar__status-btn"
                onClick={canOpenStatusPanel ? openStatusModal : undefined}
                disabled={!canOpenStatusPanel}
              >
                <PackageSearch size={16} />
                {statusPanelButtonLabel}
              </button>
            </div>
          </aside>

            {showRequestModal && (
              <div className="digital-id-request-modal" role="dialog" aria-modal="true" aria-label="Request Physical ID Card">
                <button
                  type="button"
                  className="digital-id-request-modal__backdrop"
                  onClick={() => setShowRequestModal(false)}
                  aria-label="Close request physical ID dialog"
                />
                <div className="digital-id-request-modal__panel">
                  <header className="digital-id-request-modal__header">
                    <div>
                      <h3 className="digital-id-request-modal__title">Request Physical ID Card</h3>
                      <p className="digital-id-request-modal__subtitle">
                        Choose your preferred fulfillment method and review your payment details.
                      </p>
                    </div>
                    <button
                      type="button"
                      className="digital-id-request-modal__close"
                      onClick={() => setShowRequestModal(false)}
                      aria-label="Close request physical ID dialog"
                    >
                      <X size={20} />
                    </button>
                  </header>

                  <div className="digital-id-request-modal__body">
                    <div className="digital-id-request-modal__section">
                      <p className="digital-id-request-modal__section-title">Fulfillment Method</p>
                      <div className="digital-id-request-modal__method-list">
                        <label className={`digital-id-request-modal__method-option${requestForm.deliveryMethod === 'pickup' ? ' is-selected' : ''}`}>
                          <span className="digital-id-request-modal__method-head">
                            <input
                              type="radio"
                              name="deliveryMethod"
                              value="pickup"
                              checked={requestForm.deliveryMethod === 'pickup'}
                              onChange={(event) => setRequestForm((prev) => ({ ...prev, deliveryMethod: event.target.value }))}
                              disabled={requestLoading}
                            />
                            <span className="digital-id-request-modal__method-label">Pickup at Office</span>
                          </span>
                          <span className="digital-id-request-modal__method-meta">Cash Payment</span>
                          <span className="digital-id-request-modal__method-total">Total: {formatPeso(pricing.pickupTotalPhp)}</span>
                        </label>

                        <label className={`digital-id-request-modal__method-option${requestForm.deliveryMethod === 'delivery' ? ' is-selected' : ''}`}>
                          <span className="digital-id-request-modal__method-head">
                            <input
                              type="radio"
                              name="deliveryMethod"
                              value="delivery"
                              checked={requestForm.deliveryMethod === 'delivery'}
                              onChange={(event) => setRequestForm((prev) => ({ ...prev, deliveryMethod: event.target.value }))}
                              disabled={requestLoading}
                            />
                            <span className="digital-id-request-modal__method-label">Ship to Address</span>
                          </span>
                          <span className="digital-id-request-modal__method-meta">Stripe Checkout</span>
                          <span className="digital-id-request-modal__method-total">Total: {formatPeso(pricing.deliveryTotalPhp)}</span>
                        </label>
                      </div>
                    </div>

                    {requestForm.deliveryMethod === 'delivery' ? (
                      <div className="digital-id-request-modal__address-box">
                        <label className="digital-id-request-modal__checkbox-row">
                          <input
                            type="checkbox"
                            checked={requestForm.useRegisteredAddress}
                            onChange={(event) => setRequestForm((prev) => ({
                              ...prev,
                              useRegisteredAddress: event.target.checked,
                              deliveryAddress: event.target.checked ? registeredAddress : prev.deliveryAddress,
                            }))}
                            disabled={requestLoading}
                          />
                          <span>Use my registered address</span>
                        </label>
                        <textarea
                          className="digital-id-request-modal__textarea"
                          value={requestForm.useRegisteredAddress ? registeredAddress : requestForm.deliveryAddress}
                          onChange={(event) => setRequestForm((prev) => ({ ...prev, deliveryAddress: event.target.value }))}
                          disabled={requestLoading || requestForm.useRegisteredAddress}
                          placeholder="Enter full delivery address"
                          rows={3}
                        />
                      </div>
                    ) : null}

                    <div className="digital-id-request-modal__pricing-box">
                      <div className="digital-id-request-modal__pricing-row">
                        <span>Base ID Fee</span>
                        <strong>{formatPeso(pricing.baseFeePhp)}</strong>
                      </div>
                      <div className="digital-id-request-modal__pricing-row">
                        <span>Shipping Fee</span>
                        <strong>{formatPeso(pricing.shippingFeePhp)}</strong>
                      </div>
                      <div className="digital-id-request-modal__pricing-row digital-id-request-modal__pricing-row--total">
                        <span>Total</span>
                        <strong>{formatPeso(selectedTotalPhp)}</strong>
                      </div>
                    </div>

                    {pricing.error ? (
                      <p className="digital-id-request-modal__error">{pricing.error}</p>
                    ) : null}

                    <div className="digital-id-request-modal__section">
                      <p className="digital-id-request-modal__section-title">Before You Proceed</p>
                      <ul className="digital-id-request-modal__notes">
                        <li>A printing fee will be charged for physical ID card processing.</li>
                        <li>Pickup requests are paid in cash at office release.</li>
                        <li>Delivery requests are paid online through Stripe Checkout.</li>
                        <li>Stripe Checkout supports cards and wallet options (Apple Pay/Google Pay) when available.</li>
                        <li>You will need to claim your ID card personally at the office once it is ready.</li>
                        <li>You will be notified via email when your card is ready for pickup.</li>
                      </ul>
                    </div>
                  </div>

                  <footer className="digital-id-request-modal__footer">
                    <button
                      type="button"
                      onClick={() => setShowRequestModal(false)}
                      className="button button--secondary digital-id-request-modal__btn"
                      disabled={requestLoading}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleRequestPhysicalID}
                      className="button button--primary digital-id-request-modal__btn"
                      disabled={requestLoading || pricing.loading}
                    >
                      {requestLoading
                        ? 'Processing...'
                        : requestForm.deliveryMethod === 'delivery'
                          ? 'Continue to Stripe'
                          : 'Submit Cash Pickup Request'}
                    </button>
                  </footer>
                </div>
              </div>
            )}

            {statusModal.open && (
              <div className="digital-id-status-modal" role="dialog" aria-modal="true" aria-label="Physical ID Card Status">
                <button
                  type="button"
                  className="digital-id-status-modal__backdrop"
                  onClick={closeStatusModal}
                  aria-label="Close status dialog"
                />
                <div className="digital-id-status-modal__panel">
                  <header className="digital-id-status-modal__header">
                    <div>
                      <h3 className="digital-id-status-modal__title">Physical ID Card Status</h3>
                      <p className="digital-id-status-modal__subtitle">Track your request progress, payment, and delivery updates.</p>
                    </div>
                  <button
                    type="button"
                    onClick={closeStatusModal}
                    className="digital-id-status-modal__close"
                    aria-label="Close status dialog"
                  >
                    <X size={20} />
                  </button>
                  </header>

                  {statusModal.loading ? (
                    <div className="digital-id-status-modal__state">
                      Loading request details...
                    </div>
                  ) : statusModal.error ? (
                    <div className="digital-id-status-modal__state">
                      <p className="digital-id-status-modal__error">{statusModal.error}</p>
                      <button
                        type="button"
                        onClick={openStatusModal}
                        className="button button--secondary digital-id-status-modal__btn"
                      >
                        Retry
                      </button>
                    </div>
                  ) : (
                    <div className="digital-id-status-modal__body">
                      {(() => {
                        const normalizedStatus = normalizeCardStatus(statusModal.status);
                        const isReleased = normalizedStatus === 'released';
                        const isRejected = normalizedStatus === 'rejected';
                        const normalizedPaymentStatus = normalizeCardStatus(statusModal.paymentStatus || 'unpaid');
                        const paymentMethod = normalizeCardStatus(statusModal.paymentMethod || 'cash');
                        const canRetryPayment = !isRejected && paymentMethod === 'stripe' && ['unpaid', 'failed', 'expired'].includes(normalizedPaymentStatus);
                        const paymentMethodLabel = statusModal.paymentMethodLabel
                          || (paymentMethod === 'stripe' ? 'Paid Online via Stripe' : 'Pay Cash on Pickup');
                        const paymentStatusLabel = statusModal.paymentStatusLabel || formatStatus(normalizedPaymentStatus || 'unpaid');
                        const officialReceiptUrl = statusModal.paymentReceiptUrl || statusModal.paymentInvoiceUrl;
                        const isDeliveryFlow = normalizeCardStatus(statusModal.deliveryMethod) === 'delivery';
                        const deliveryContactNumber = statusModal.contactNumber || user?.phone || null;
                        const shippingStatus = normalizeCardStatus(statusModal.shippingStatus || 'preparing') || 'preparing';
                        const shippingSteps = ['preparing', 'shipped', 'delivered'];
                        const activeShippingIndex = shippingSteps.indexOf(shippingStatus);
                        return (
                      <div style={{ display: 'grid', gap: '0.875rem' }}>
                        {/* Request ID */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ color: '#4b5563', fontSize: '0.9rem' }}>Request ID</span>
                          <span style={{
                            fontWeight: 700,
                            color: '#111827',
                            fontFamily: 'var(--font-family-mono)',
                            fontSize: '0.9rem',
                          }}>
                            {statusModal.requestNo || '—'}
                          </span>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ color: '#4b5563', fontSize: '0.9rem' }}>Payment Method</span>
                          <span style={{ fontWeight: 600, color: '#0f172a', fontSize: '0.85rem' }}>
                            {paymentMethodLabel}
                          </span>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ color: '#4b5563', fontSize: '0.9rem' }}>Total Amount</span>
                          <span style={{ fontWeight: 700, color: '#0f172a', fontSize: '0.9rem' }}>
                            {formatPeso(statusModal.totalAmountPhp)}
                          </span>
                        </div>

                        {/* Status with colored badge */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ color: '#4b5563', fontSize: '0.9rem' }}>Status</span>
                          <span style={{
                            fontWeight: 700,
                            fontSize: '0.8rem',
                            padding: '0.25rem 0.75rem',
                            borderRadius: '9999px',
                            ...(normalizedStatus === 'pending' ? { backgroundColor: '#fef3c7', color: '#92400e' } :
                              normalizedStatus === 'printing' ? { backgroundColor: '#dbeafe', color: '#1e40af' } :
                              normalizedStatus === 'ready_for_pickup' ? { backgroundColor: '#d1fae5', color: '#065f46' } :
                              normalizedStatus === 'released' ? { backgroundColor: '#ede9fe', color: '#5b21b6' } :
                              normalizedStatus === 'cancelled' ? { backgroundColor: '#fee2e2', color: '#991b1b' } :
                              normalizedStatus === 'rejected' ? { backgroundColor: '#fee2e2', color: '#991b1b' } :
                              { backgroundColor: '#f3f4f6', color: '#374151' }),
                          }}>
                            {statusModal.statusLabel || formatStatus(statusModal.status)}
                          </span>
                        </div>

                        {/* Rejection & Refund Alert Block */}
                        {normalizedStatus === 'rejected' && statusModal.rejectionReason ? (
                          <div style={{
                            border: '1px solid #fca5a5',
                            borderRadius: '0.5rem',
                            padding: '1rem',
                            backgroundColor: '#fef2f2',
                            display: 'grid',
                            gap: '0.75rem',
                          }}>
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                            }}>
                              <AlertCircle size={20} style={{ color: '#dc2626', flexShrink: 0 }} />
                              <span style={{ fontSize: '0.95rem', fontWeight: 700, color: '#991b1b' }}>
                                Request Rejected {statusModal.refundAmountPhp ? '& Refunded' : ''}
                              </span>
                            </div>

                            <div style={{ paddingLeft: 28, display: 'grid', gap: '0.5rem' }}>
                              <div>
                                <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#991b1b' }}>Reason: </span>
                                <span style={{ fontSize: '0.85rem', color: '#7f1d1d' }}>{statusModal.rejectionReason}</span>
                              </div>

                              {statusModal.refundAmountPhp ? (
                                <div style={{
                                  border: '1px solid #fca5a5',
                                  borderRadius: '0.375rem',
                                  padding: '0.65rem 0.75rem',
                                  backgroundColor: '#fff1f2',
                                  display: 'grid',
                                  gap: '0.35rem',
                                }}>
                                  <div>
                                    <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#991b1b' }}>Refund Amount: </span>
                                    <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#7f1d1d' }}>
                                      {formatPeso(statusModal.refundAmountPhp)}
                                    </span>
                                  </div>
                                  <span style={{ fontSize: '0.82rem', color: '#9f1239', lineHeight: 1.5 }}>
                                    Funds usually appear in your account within 5-10 business days.
                                  </span>
                                </div>
                              ) : null}
                            </div>
                          </div>
                        ) : null}

                        {/* Pickup Code - hidden once released or rejected */}
                        {!isReleased && normalizedStatus !== 'rejected' ? (
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '0.75rem',
                            backgroundColor: statusModal.pickupCode ? '#f0fdf4' : '#f9fafb',
                            borderRadius: '0.375rem',
                            border: statusModal.pickupCode ? '1px solid #bbf7d0' : '1px solid #e5e7eb',
                          }}>
                            <span style={{ color: '#4b5563', fontSize: '0.9rem', fontWeight: 500 }}>Pickup Code</span>
                            <span style={{
                              fontWeight: 700,
                              fontSize: statusModal.pickupCode ? '1.1rem' : '0.85rem',
                              color: statusModal.pickupCode ? '#065f46' : '#9ca3af',
                              fontFamily: statusModal.pickupCode ? 'var(--font-family-mono)' : 'inherit',
                              letterSpacing: statusModal.pickupCode ? '0.05em' : 'normal',
                            }}>
                              {statusModal.pickupCode || 'Not yet assigned'}
                            </span>
                          </div>
                        ) : null}

                        {/* Payment Status */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ color: '#4b5563', fontSize: '0.9rem' }}>Payment</span>
                          <span style={{
                            fontWeight: 600,
                            fontSize: '0.8rem',
                            padding: '0.2rem 0.6rem',
                            borderRadius: '9999px',
                            ...(normalizedPaymentStatus === 'paid' ? { backgroundColor: '#d1fae5', color: '#065f46' } :
                              normalizedPaymentStatus === 'waived' ? { backgroundColor: '#ede9fe', color: '#5b21b6' } :
                              normalizedPaymentStatus === 'processing' ? { backgroundColor: '#dbeafe', color: '#1e40af' } :
                              normalizedPaymentStatus === 'failed' ? { backgroundColor: '#fee2e2', color: '#991b1b' } :
                              normalizedPaymentStatus === 'expired' ? { backgroundColor: '#f3f4f6', color: '#374151' } :
                              { backgroundColor: '#fef3c7', color: '#92400e' }),
                          }}>
                            {paymentStatusLabel}
                          </span>
                        </div>

                        {officialReceiptUrl && normalizedPaymentStatus === 'paid' ? (
                          <div style={{
                            padding: '0.75rem',
                            borderRadius: '0.375rem',
                            border: '1px solid #bbf7d0',
                            backgroundColor: '#f0fdf4',
                            display: 'grid',
                            gap: '0.4rem',
                          }}>
                            <span style={{ fontSize: '0.88rem', color: '#166534' }}>
                              Payment receipt is available from Stripe.
                            </span>
                            <a
                              href={officialReceiptUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: '#166534', fontWeight: 700, textDecoration: 'underline', fontSize: '0.88rem' }}
                            >
                              View Official Receipt
                            </a>
                          </div>
                        ) : null}

                        {isDeliveryFlow ? (
                          <div className="digital-id-shipping-tracker">
                            <span className="digital-id-shipping-tracker__title">Shipping Tracker</span>
                            <div className="digital-id-shipping-tracker__steps">
                              {shippingSteps.map((step, index) => {
                                const isDone = activeShippingIndex > index;
                                const isCurrent = activeShippingIndex === index || (activeShippingIndex < 0 && index === 0);
                                const StepIcon = step === 'preparing' ? Package : step === 'shipped' ? Truck : Check;

                                return (
                                  <div
                                    key={step}
                                    className={`digital-id-shipping-tracker__step${isDone ? ' is-done' : ''}${isCurrent ? ' is-current' : ''}${!isDone && !isCurrent ? ' is-pending' : ''}`}
                                  >
                                    <span className="digital-id-shipping-tracker__icon">
                                      <StepIcon size={15} />
                                    </span>
                                    <span className="digital-id-shipping-tracker__label">
                                      {step === 'preparing' ? 'Preparing' : step === 'shipped' ? 'Shipping' : 'Delivered'}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                            {statusModal.trackingNumber ? (
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ color: '#4b5563', fontSize: '0.85rem' }}>Tracking Number</span>
                                <span style={{ fontFamily: 'monospace', fontWeight: 700, color: '#0f172a', fontSize: '0.85rem' }}>
                                  {statusModal.trackingNumber}
                                </span>
                              </div>
                            ) : null}
                          </div>
                        ) : null}

                        {isDeliveryFlow ? (
                          <div style={{
                            border: '1px solid #e2e8f0',
                            borderRadius: '0.5rem',
                            padding: '0.9rem',
                            display: 'grid',
                            gap: '0.7rem',
                            backgroundColor: '#ffffff',
                          }}>
                            <span style={{ fontSize: '0.9rem', fontWeight: 700, color: '#0f172a' }}>Delivery Details</span>
                            <div style={{ display: 'grid', gap: '0.3rem' }}>
                              <span style={{ color: '#4b5563', fontSize: '0.83rem' }}>Delivery Address</span>
                              <span style={{ color: '#0f172a', fontSize: '0.87rem', lineHeight: 1.45 }}>
                                {statusModal.deliveryAddress || 'Not available'}
                              </span>
                            </div>
                            <div style={{ display: 'grid', gap: '0.3rem' }}>
                              <span style={{ color: '#4b5563', fontSize: '0.83rem' }}>Contact Number</span>
                              <span style={{ color: '#0f172a', fontSize: '0.87rem', fontWeight: 600 }}>
                                {deliveryContactNumber || 'Not available'}
                              </span>
                            </div>
                          </div>
                        ) : null}

                        {canRetryPayment ? (
                          <div style={{
                            padding: '0.75rem',
                            borderRadius: '0.375rem',
                            border: '1px solid #bfdbfe',
                            backgroundColor: '#eff6ff',
                            display: 'grid',
                            gap: '0.5rem',
                          }}>
                            <span style={{ fontSize: '0.88rem', color: '#1e3a8a' }}>
                              Your ID request is active but payment is not complete yet.
                            </span>
                            <button
                              onClick={handleRetryIdPayment}
                              disabled={requestLoading}
                              style={{
                                justifySelf: 'flex-start',
                                padding: '0.45rem 0.9rem',
                                borderRadius: '0.375rem',
                                border: 'none',
                                backgroundColor: '#2563eb',
                                color: 'white',
                                cursor: requestLoading ? 'not-allowed' : 'pointer',
                                opacity: requestLoading ? 0.7 : 1,
                                fontWeight: 600,
                              }}
                            >
                              {requestLoading ? 'Redirecting...' : normalizedPaymentStatus === 'unpaid' ? 'Pay Now' : 'Retry Payment'}
                            </button>
                          </div>
                        ) : null}

                        <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '0.75rem', display: 'grid', gap: '0.5rem' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ color: '#6b7280', fontSize: '0.85rem' }}>Requested</span>
                            <span style={{ fontWeight: 500, color: '#374151', fontSize: '0.85rem' }}>
                              {formatDateTime(statusModal.requestedAt)}
                            </span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ color: '#6b7280', fontSize: '0.85rem' }}>Last Updated</span>
                            <span style={{ fontWeight: 500, color: '#374151', fontSize: '0.85rem' }}>
                              {formatDateTime(statusModal.updatedAt)}
                            </span>
                          </div>
                          {statusModal.paidAt ? (
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span style={{ color: '#6b7280', fontSize: '0.85rem' }}>Paid At</span>
                              <span style={{ fontWeight: 500, color: '#374151', fontSize: '0.85rem' }}>
                                {formatDateTime(statusModal.paidAt)}
                              </span>
                            </div>
                          ) : null}
                        </div>
                      </div>
                        );
                      })()}

                      <div className="digital-id-status-modal__footer">
                        <button
                          type="button"
                          onClick={openStatusModal}
                          className="button button--secondary digital-id-status-modal__btn"
                        >
                          Refresh
                        </button>
                        <button
                          type="button"
                          onClick={closeStatusModal}
                          className="button button--primary digital-id-status-modal__btn"
                        >
                          Close
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {cashRequestModal.open && (
              <div className="digital-id-cash-modal" role="dialog" aria-modal="true" aria-label="Cash payment request submitted">
                <button
                  type="button"
                  className="digital-id-cash-modal__backdrop"
                  onClick={closeCashRequestModal}
                  aria-label="Close confirmation dialog"
                />
                <div className="digital-id-cash-modal__panel">
                  <header className="digital-id-cash-modal__header">
                    <h3 className="digital-id-cash-modal__title">Request Submitted</h3>
                    <button
                      type="button"
                      className="digital-id-cash-modal__close"
                      onClick={closeCashRequestModal}
                      aria-label="Close confirmation dialog"
                    >
                      <X size={20} />
                    </button>
                  </header>
                  <div className="digital-id-cash-modal__body">
                    <p className="digital-id-cash-modal__message">{cashRequestModal.message}</p>
                    <p className="digital-id-cash-modal__hint">You can track updates anytime from the request status panel.</p>
                  </div>
                  <footer className="digital-id-cash-modal__footer">
                    <button
                      type="button"
                      onClick={closeCashRequestModal}
                      className="button button--secondary digital-id-cash-modal__btn"
                    >
                      Close
                    </button>
                    <button
                      type="button"
                      onClick={async () => {
                        closeCashRequestModal();
                        await openStatusModal();
                      }}
                      className="button button--primary digital-id-cash-modal__btn"
                    >
                      View Request Status
                    </button>
                  </footer>
                </div>
              </div>
            )}

        </div>
      </div>
    </AppLayout>
  );
}

DigitalIdPage.propTypes = {
  user: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    position: PropTypes.string,
    company: PropTypes.string,
    photo: PropTypes.string,
    membershipDate: PropTypes.string,
    digitalId: PropTypes.string,
    qrCode: PropTypes.string,
    qrReference: PropTypes.string,
    isApproved: PropTypes.bool,
    hasPendingIdRequest: PropTypes.bool,
    hasAnyIdRequest: PropTypes.bool,
  }).isRequired,
  onLogout: PropTypes.func.isRequired,
};
